import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import type {
  ConversationRow,
  DirectMessageRow,
  Paginated,
} from "@/lib/types";

// ---------------------------------------------------------------------------
// Direct messages — talks to /api/v1/conversations/*. Shape mirrors the
// forum hooks (useThreads / usePosts / useCreatePost) so the conversation
// list and message stream feel familiar.
// ---------------------------------------------------------------------------

/** GET /api/v1/conversations/ — list my conversations with last-message
 *  preview + unread count. Sorted server-side by Conversation.updated_at
 *  DESC so the most-recently-active chat is first. */
export function useConversations() {
  return useQuery<Paginated<ConversationRow>>({
    queryKey: ["conversations", "list"],
    queryFn: async () => {
      const { data } = await api.get<Paginated<ConversationRow>>(
        "/conversations/",
      );
      return data;
    },
  });
}

/** POST /api/v1/conversations/ — get-or-create a 1-on-1 DM with
 *  `userId`. Idempotent: same pair always returns the same conversation.
 *  Returns the ConversationRow on success. */
export function useStartConversation() {
  const qc = useQueryClient();
  return useMutation<ConversationRow, Error, string>({
    mutationFn: async (userId: string) => {
      const { data } = await api.post<ConversationRow>("/conversations/", {
        user_id: userId,
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["conversations"] }),
  });
}

/** GET /api/v1/conversations/<id>/messages/ — full transcript. Pagination
 *  is OFF on the backend (chat-style) so this returns every message at
 *  once; if conversations ever get big enough that's a perf issue, switch
 *  to cursor pagination + "load older" on the client. */
export function useConversationMessages(conversationId: string | undefined) {
  return useQuery<Paginated<DirectMessageRow>>({
    enabled: !!conversationId,
    queryKey: ["conversations", "messages", conversationId],
    queryFn: async () => {
      const { data } = await api.get<Paginated<DirectMessageRow>>(
        `/conversations/${conversationId}/messages/`,
      );
      return data;
    },
  });
}

/** POST /api/v1/conversations/<id>/messages/ — send a message. Multipart
 *  when files are attached so the same axios pipeline + onUploadProgress
 *  callback the forum composer uses works here unchanged. */
export function useSendDirectMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      conversationId: string;
      content: string;
      files?: File[];
      onUploadProgress?: (percent: number) => void;
    }) => {
      if (input.files && input.files.length > 0) {
        const fd = new FormData();
        fd.append("content", input.content);
        for (const f of input.files) fd.append("attachments", f);
        const { data } = await api.post<DirectMessageRow>(
          `/conversations/${input.conversationId}/messages/`,
          fd,
          {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (evt) => {
              if (!input.onUploadProgress) return;
              const total = evt.total ?? 0;
              if (!total) { input.onUploadProgress(-1); return; }
              input.onUploadProgress(Math.round((evt.loaded / total) * 100));
            },
          },
        );
        return data;
      }
      const { data } = await api.post<DirectMessageRow>(
        `/conversations/${input.conversationId}/messages/`,
        { content: input.content },
      );
      return data;
    },
    onSuccess: (_msg, vars) => {
      qc.invalidateQueries({ queryKey: ["conversations", "messages", vars.conversationId] });
      // Bumps the conversation in the list + clears my own unread count.
      qc.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

/** Sum of `unread_count` across every conversation the user is in.
 *  Drives the sidebar Messages badge. Reuses the conversations-list
 *  query key, so React Query dedupes with the Messages page's request
 *  and the WS dm.message handler's invalidation auto-bumps this in
 *  real time. */
export function useTotalUnreadDMCount(): number {
  const { data } = useConversations();
  if (!data) return 0;
  return data.results.reduce((sum, c) => sum + (c.unread_count || 0), 0);
}

/** POST /api/v1/conversations/<id>/read/ — mark conversation as read up
 *  to now(). The unread badge clears on the next conversation-list
 *  refetch. Called when the user actually opens a conversation. */
export function useMarkConversationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string) => {
      await api.post(`/conversations/${conversationId}/read/`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["conversations"] }),
  });
}
